import { useApolloClient } from '@apollo/client';

import {
  useGetMasterClassUserSessionQuery,
  useUpdateMasterClassUserSessionMutation,
  useCreateMasterClassUserSessionMutation,
  useResetCaseProgressMutation,
  GetMasterClassUserSessionDocument,
} from '@/@generated/codegen/API';

export const CASE_PROGRESS_QUERY_KEYS = {
  byUserId: (userId: string) => ['case_progress', 'user', userId] as const,
  byUserAndCase: (userId: string, caseId: string) =>
    ['case_progress', 'user', userId, 'case', caseId] as const,
};

/**
 * Hook to query case progress for a specific user, masterclass, and case.
 *
 * This is the main hook used to fetch individual case progress.
 * Returns null for data if session doesn't exist (handles "not found" errors gracefully).
 */
export const useCaseProgressByMasterclassAndCase = (
  userId: string | null,
  masterclassId: string | null,
  caseId: string | null
) => {
  const { data, loading, error, refetch } = useGetMasterClassUserSessionQuery({
    variables: {
      where: {
        // Use individual field filters instead of compound unique constraint
        // because prismaCaslService.findOne uses findFirst which doesn't support compound unique
        userId: { equals: userId! },
        masterClassId: { equals: masterclassId! },
        caseId: { equals: caseId! },
      },
    },
    skip: !userId || !masterclassId || !caseId,
    // Don't throw on error - we handle "not found" as null data
    errorPolicy: 'all',
  });

  // Check if error is a "not found" error - treat as null data
  const isNotFoundError = error?.message?.toLowerCase().includes('not found');

  // Return GraphQL response directly - already camelCase
  const sessionData = data?.getMasterClassUserSession
    ? {
        id: data.getMasterClassUserSession.id,
        userId: data.getMasterClassUserSession.userId,
        caseId: data.getMasterClassUserSession.caseId,
        currentSlideId: data.getMasterClassUserSession.currentSlideId,
        visitedSlideIds: data.getMasterClassUserSession.visitedSlideIds || [],
        completed: data.getMasterClassUserSession.completed,
        celebrationShown: data.getMasterClassUserSession.celebrationShown,
        scorePercentage: data.getMasterClassUserSession.scorePercentage,
      }
    : null;

  return {
    data: sessionData,
    isLoading: loading,
    // Only return error if it's not a "not found" error
    error: isNotFoundError ? null : error,
    refetch,
  };
};

/**
 * @deprecated Use useCaseProgressByMasterclassAndCase instead
 * Kept for backward compatibility during migration
 */
export const useCaseProgressByCase = useCaseProgressByMasterclassAndCase;

/**
 * Hook to create or update case progress.
 *
 * Handles both creation (if session doesn't exist) and updates (if it does).
 */
export const useUpsertCaseProgress = () => {
  const apolloClient = useApolloClient();
  const [createSession] = useCreateMasterClassUserSessionMutation();
  const [updateSession] = useUpdateMasterClassUserSessionMutation();

  return {
    mutateAsync: async ({
      userId,
      masterclassId,
      caseId,
      updates,
    }: {
      userId: string;
      /** The MasterClass ID (not PatientCase ID) */
      masterclassId: string;
      /** The PatientCase ID */
      caseId: string;
      updates: {
        current_slide_id?: string | null;
        visited_slide_ids?: string[];
        completed?: boolean;
        celebration_shown?: boolean;
        score_percentage?: number;
      };
    }) => {
      // First, try to get existing session (may return null or throw error if not found)
      let existingSession = null;
      try {
        const { data: existingData } = await apolloClient.query({
          query: GetMasterClassUserSessionDocument,
          variables: {
            where: {
              // Use individual field filters instead of compound unique
              userId: { equals: userId },
              masterClassId: { equals: masterclassId },
              caseId: { equals: caseId },
            },
          },
          fetchPolicy: 'network-only',
        });
        existingSession = existingData?.getMasterClassUserSession;
      } catch (error) {
        // Session doesn't exist - this is expected for new users
        console.log('[useUpsertCaseProgress] No existing session found, will create new one');
      }

      if (existingSession) {
        // Update existing session
        const { data } = await updateSession({
          variables: {
            data: {
              id: existingSession.id,
              ...(updates.current_slide_id !== undefined && {
                currentSlideId: { set: updates.current_slide_id },
              }),
              ...(updates.visited_slide_ids !== undefined && {
                visitedSlideIds: updates.visited_slide_ids,
              }),
              ...(updates.completed !== undefined && {
                completed: { set: updates.completed },
              }),
              ...(updates.celebration_shown !== undefined && {
                celebrationShown: { set: updates.celebration_shown },
              }),
              ...(updates.score_percentage !== undefined && {
                scorePercentage: { set: updates.score_percentage },
              }),
            },
          },
        });

        // Return GraphQL response directly - already camelCase
        return {
          id: data!.updateMasterClassUserSession.id,
          userId: data!.updateMasterClassUserSession.userId,
          caseId: data!.updateMasterClassUserSession.caseId,
          currentSlideId: data!.updateMasterClassUserSession.currentSlideId,
          visitedSlideIds: data!.updateMasterClassUserSession.visitedSlideIds,
          completed: data!.updateMasterClassUserSession.completed,
          celebrationShown: data!.updateMasterClassUserSession.celebrationShown,
          scorePercentage: data!.updateMasterClassUserSession.scorePercentage,
        };
      } else {
        // Create new session
        const { data } = await createSession({
          variables: {
            data: {
              masterClassId: masterclassId,
              caseId: caseId,
            },
          },
        });

        // Now update it with the provided values
        const { data: updatedData } = await updateSession({
          variables: {
            data: {
              id: data!.createMasterClassUserSession.id,
              ...(updates.current_slide_id !== undefined && {
                currentSlideId: { set: updates.current_slide_id },
              }),
              ...(updates.visited_slide_ids !== undefined && {
                visitedSlideIds: updates.visited_slide_ids,
              }),
              ...(updates.completed !== undefined && {
                completed: { set: updates.completed },
              }),
              ...(updates.celebration_shown !== undefined && {
                celebrationShown: { set: updates.celebration_shown },
              }),
              ...(updates.score_percentage !== undefined && {
                scorePercentage: { set: updates.score_percentage },
              }),
            },
          },
        });

        // Return GraphQL response directly - already camelCase
        return {
          id: updatedData!.updateMasterClassUserSession.id,
          userId: updatedData!.updateMasterClassUserSession.userId,
          caseId: updatedData!.updateMasterClassUserSession.caseId,
          currentSlideId: updatedData!.updateMasterClassUserSession.currentSlideId,
          visitedSlideIds: updatedData!.updateMasterClassUserSession.visitedSlideIds,
          completed: updatedData!.updateMasterClassUserSession.completed,
          celebrationShown: updatedData!.updateMasterClassUserSession.celebrationShown,
          scorePercentage: updatedData!.updateMasterClassUserSession.scorePercentage,
        };
      }
    },
  };
};

/**
 * Hook to reset case progress to initial state.
 *
 * Calls backend mutation that atomically resets session AND deletes answers.
 * After reset, invalidates Apollo cache to ensure UI reflects the reset state.
 */
export const useResetCaseProgress = () => {
  const apolloClient = useApolloClient();
  const [resetMutation] = useResetCaseProgressMutation();

  return {
    mutateAsync: async ({ masterclassId, caseId }: { masterclassId: string; caseId: string }) => {
      await resetMutation({
        variables: {
          data: { masterClassId: masterclassId, caseId },
        },
      });

      // Invalidate Apollo cache to ensure UI reflects reset state
      // 1. Evict answers for this case from cache
      apolloClient.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'listAnswers',
      });

      // 2. Evict session cache for this case
      apolloClient.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'getMasterClassUserSession',
      });

      // Garbage collect orphaned references
      apolloClient.cache.gc();
    },
  };
};
